import {
  KdsModifierStyle,
  ModifierKeywordMatchMode,
} from '@oolio-group/domain';

export interface MatchedModifierStyle {
  color?: string;
  italic?: boolean;
  background?: boolean;
}

/**
 * Returns the active modifier styles in the precedence order matching uses:
 * keyword-configured styles first (by ascending `priority`, the same order
 * the settings list shows them), then `matchAll` styles last (also by
 * ascending `priority` among themselves). There's no priority-editing UI —
 * new styles always append at the lowest precedence — so without this,
 * a `matchAll` style would permanently shadow any keyword style created
 * after it. Sorting `matchAll` last guarantees a keyword-configured style
 * always overrides a broader `matchAll` style, regardless of creation
 * order. Inactive styles (`isActive === false`) are dropped. Callers on a
 * hot render path can memoize this once per `styles` reference and pass
 * the result to {@link matchInSortedModifierStyles} instead of re-sorting
 * per modifier.
 */
export function sortActiveModifierStyles(
  styles: KdsModifierStyle[] | undefined | null,
): KdsModifierStyle[] {
  return (styles ?? [])
    .filter(style => style.isActive !== false)
    .slice()
    .sort((a, b) => {
      if (!!a.matchAll !== !!b.matchAll) {
        return a.matchAll ? 1 : -1;
      }
      return a.priority - b.priority;
    });
}

/**
 * Finds the modifier style that should apply to a modifier line, given the
 * already-sorted active styles. The first style that matches wins — either a
 * `matchAll` style (matches unconditionally, even for a blank modifier name)
 * or one whose non-blank keyword matches. A modifier that matches none
 * returns `undefined`.
 */
export function matchInSortedModifierStyles(
  modifierName: string | undefined,
  sortedActiveStyles: KdsModifierStyle[],
): MatchedModifierStyle | undefined {
  if (!sortedActiveStyles.length) return undefined;
  const normalized = (modifierName || '').trim().toLowerCase();

  for (const style of sortedActiveStyles) {
    const matches =
      style.matchAll ||
      (!!normalized &&
        style.conditions?.some(condition => {
          const keyword = (condition?.keyword || '').trim().toLowerCase();
          if (!keyword) return false;
          return condition.matchType === ModifierKeywordMatchMode.EXACT
            ? normalized === keyword
            : normalized.includes(keyword);
        }));

    if (matches) {
      return {
        color: style.styles?.color,
        italic: style.styles?.italic,
        background: style.styles?.background,
      };
    }
  }

  return undefined;
}

/**
 * Finds the modifier style that should apply to a modifier line on a docket.
 *
 * Keyword-configured styles are considered first (by ascending `priority`),
 * then `matchAll` styles last, and the first active style that matches wins.
 * A modifier that matches no style returns `undefined` and keeps its default
 * appearance.
 */
export function matchModifierStyle(
  modifierName: string | undefined,
  styles: KdsModifierStyle[] | undefined | null,
): MatchedModifierStyle | undefined {
  return matchInSortedModifierStyles(
    modifierName,
    sortActiveModifierStyles(styles),
  );
}
